import { defineStore } from 'pinia';
import {
  ApiError,
  clearAntiforgeryToken,
  loadAntiforgeryToken,
  setAntiforgeryToken
} from '../api/client';
import type { EntityId } from '../api/entityId';
import {
  InfrastructureService,
  PageBootstrapKind,
  PermissionKind,
  QueriesService,
  type GetCurrentUserProfileQueryResponse,
  type GetPageBootstrapQuery,
  type GetPageBootstrapQueryResponse,
  type GetPublicSettingsQueryResponse
} from '../api/generated';

interface SessionState {
  initialized: boolean;
  loading: boolean;
  profile: GetCurrentUserProfileQueryResponse | null;
  publicSettings: GetPublicSettingsQueryResponse | null;
  pageBootstrap: GetPageBootstrapQueryResponse | null;
  pageBootstrapKey: string | null;
  error: string | null;
}

interface LoadProfileOptions {
  force?: boolean;
}

let bootstrapPromise: Promise<void> | null = null;
let profileLoadPromise: Promise<void> | null = null;
let publicSettingsLoadPromise: Promise<void> | null = null;
let pageBootstrapPromise: Promise<void> | null = null;
const lastUserIdKey = 'knowledgebase.lastUserId.v1';

export const useSessionStore = defineStore('session', {
  state: (): SessionState => ({
    initialized: false,
    loading: false,
    profile: null,
    publicSettings: null,
    pageBootstrap: null,
    pageBootstrapKey: null,
    error: null
  }),
  getters: {
    isAuthenticated: state => state.profile != null,
    isGlobalAdministrator: state => state.profile?.globalPermissions?.includes(PermissionKind.ADMIN_ACCESS) === true,
    canUseAdministration: state => {
      const permissions = state.profile?.globalPermissions ?? [];
      return permissions.includes(PermissionKind.ADMIN_ACCESS)
        || permissions.includes(PermissionKind.CREATE_PROJECT)
        || permissions.includes(PermissionKind.MANAGE_PROJECT_ACCESS)
        || permissions.includes(PermissionKind.MANAGE_ROLES)
        || permissions.includes(PermissionKind.MANAGE_SETTINGS)
        || permissions.includes(PermissionKind.MANAGE_USERS);
    },
    hasGlobalPermission: state => (permission: PermissionKind): boolean => {
      const permissions = state.profile?.globalPermissions ?? [];
      return permissions.includes(PermissionKind.ADMIN_ACCESS) || permissions.includes(permission);
    },
    hasAnyGlobalPermission: state => (requiredPermissions: PermissionKind | PermissionKind[]): boolean => {
      const permissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
      const grantedPermissions = state.profile?.globalPermissions ?? [];
      return grantedPermissions.includes(PermissionKind.ADMIN_ACCESS)
        || permissions.some(permission => grantedPermissions.includes(permission));
    },
    knowledgeBaseTitle: state => state.publicSettings?.knowledgeBaseTitle ?? 'Knowledge Base',
    userRegistrationEnabled: state => state.publicSettings?.userRegistrationEnabled === true
  },
  actions: {
    async bootstrap(): Promise<void> {
      if (this.initialized) {
        return;
      }

      if (bootstrapPromise) {
        await bootstrapPromise;
        return;
      }

      bootstrapPromise = this.runBootstrap();
      try {
        await bootstrapPromise;
      } finally {
        bootstrapPromise = null;
      }
    },
    async runBootstrap(): Promise<void> {
      this.loading = true;
      try {
        await this.bootstrapPage({ pageKind: PageBootstrapKind.PUBLIC }, 'Public');
      } finally {
        this.initialized = true;
        this.loading = false;
      }
    },
    async bootstrapPage(request: GetPageBootstrapQuery, key: string): Promise<void> {
      if (this.pageBootstrapKey === key && this.pageBootstrap != null) {
        return;
      }

      if (pageBootstrapPromise) {
        await pageBootstrapPromise;
        if (this.pageBootstrapKey === key && this.pageBootstrap != null) {
          return;
        }
      }

      pageBootstrapPromise = this.runBootstrapPage(request, key);
      try {
        await pageBootstrapPromise;
      } finally {
        pageBootstrapPromise = null;
      }
    },
    async runBootstrapPage(request: GetPageBootstrapQuery, key: string): Promise<void> {
      this.loading = true;
      try {
        const response = await QueriesService.queryGetPageBootstrapQuery({ requestBody: request });
        this.applyPageBootstrap(response, key);
      } catch (error) {
        this.error = getErrorMessage(error, 'Unable to load page.');
        throw error;
      } finally {
        this.loading = false;
      }
    },
    applyPageBootstrap(response: GetPageBootstrapQueryResponse, key: string): void {
      this.publicSettings = response.publicSettings;
      this.profile = response.profile ?? null;
      rememberLastUserId(this.profile?.id ?? null);
      this.pageBootstrap = response;
      this.pageBootstrapKey = key;
      this.initialized = true;
      this.error = null;
    },
    clearPageBootstrap(): void {
      this.pageBootstrap = null;
      this.pageBootstrapKey = null;
    },
    async loadPublicSettings(): Promise<void> {
      if (publicSettingsLoadPromise) {
        await publicSettingsLoadPromise;
        return;
      }

      publicSettingsLoadPromise = this.runLoadPublicSettings();
      try {
        await publicSettingsLoadPromise;
      } finally {
        publicSettingsLoadPromise = null;
      }
    },
    async runLoadPublicSettings(): Promise<void> {
      try {
        this.publicSettings = await QueriesService.queryGetPublicSettingsQuery({ requestBody: {} });
        this.error = null;
      } catch (error) {
        this.error = getErrorMessage(error, 'Unable to load public settings.');
      }
    },
    async loadProfile(options: LoadProfileOptions = {}): Promise<void> {
      if (profileLoadPromise) {
        await profileLoadPromise;
        return;
      }

      if (this.profile != null && options.force !== true) {
        return;
      }

      profileLoadPromise = this.runLoadProfile();
      try {
        await profileLoadPromise;
      } finally {
        profileLoadPromise = null;
      }
    },
    async runLoadProfile(): Promise<void> {
      try {
        this.profile = await QueriesService.queryGetCurrentUserProfileQuery({ requestBody: {} });
        rememberLastUserId(this.profile.id);
        this.error = null;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          this.profile = null;
          rememberLastUserId(null);
          return;
        }

        this.error = getErrorMessage(error, 'Unable to load profile.');
      }
    },
    async refreshAfterLogin(requestToken: string | null = null): Promise<void> {
      bootstrapPromise = null;
      profileLoadPromise = null;
      publicSettingsLoadPromise = null;
      pageBootstrapPromise = null;
      this.initialized = false;
      this.profile = null;
      this.clearPageBootstrap();
      if (requestToken != null && requestToken.length > 0) {
        setAntiforgeryToken(requestToken);
      } else {
        clearAntiforgeryToken();
        await loadAntiforgeryToken();
      }
    },
    async logout(): Promise<void> {
      await InfrastructureService.logout();
      clearAntiforgeryToken();
      this.profile = null;
      rememberLastUserId(null);
      this.clearPageBootstrap();
    }
  }
});

export function getLastSessionUserId(): EntityId | null {
  try {
    const value = window.localStorage.getItem(lastUserIdKey);
    return value != null && /^\d+$/.test(value) ? value : null;
  } catch {
    return null;
  }
}

function rememberLastUserId(userId: EntityId | null): void {
  try {
    if (userId == null) {
      window.localStorage.removeItem(lastUserIdKey);
      return;
    }

    window.localStorage.setItem(lastUserIdKey, userId);
  } catch {
    // localStorage может быть недоступен в приватном режиме; авторизация от этого не зависит.
  }
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const body = error.body as { detail?: unknown; title?: unknown } | null;
    if (typeof body?.detail === 'string' && body.detail.length > 0) {
      return body.detail;
    }

    if (typeof body?.title === 'string' && body.title.length > 0) {
      return body.title;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}
